import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    ClipboardList,
    RefreshCw,
    Search,
    Shield,
    ShieldOff,
    UserCheck,
    UserX,
} from 'lucide-react';
import { api, ApiError, buildQuery } from '../../lib/api';
import { AppIcon } from '../../components/AppIcon';
import { Modal } from '../../components/Modal';
import { EmptyState, ErrorState, LoadingState, Spinner, useToast } from '../../components/Feedback';
import {
    ACCESS_SOURCE_LABELS,
    ENTRA_REASON_LABELS,
    appColor,
    cn,
    formatDate,
    formatDateTime,
    formatRelative,
} from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import type {
    EntraSyncDetail,
    EntraSyncMeta,
    EntraSyncResult,
    EntraSyncSummary,
    HubUserDetail,
    HubUserSummary,
} from '../../types';

/**
 * Les dernières synchronisations et l'état de la configuration.
 *
 * Interrogée par la page comme par la modale : react-query dédoublonne sur la
 * clé, et l'état affiché à côté du bouton est exactement celui que la modale
 * détaille.
 */
function useSyncAnnuaire(enabled = true) {
    return useQuery({
        queryKey: ['entra-sync', 'runs'],
        queryFn: () =>
            api.getWithMeta<EntraSyncSummary[], EntraSyncMeta>('/api/admin/sync/entra'),
        enabled,
    });
}

type FiltreRole = '' | 'user' | 'admin';

export function AdminUsersPage() {
    const [search, setSearch] = useState('');
    const [role, setRole] = useState<FiltreRole>('');
    const [includeInactive, setIncludeInactive] = useState(false);
    const [detailUserId, setDetailUserId] = useState<string | null>(null);
    const [rapportOuvert, setRapportOuvert] = useState(false);

    const { user: currentUser } = useAuth();
    const queryClient = useQueryClient();
    const { notify } = useToast();

    const { data: users = [], isLoading, isError, error, refetch } = useQuery({
        queryKey: ['admin-users', search, role, includeInactive],
        queryFn: () =>
            api.get<HubUserSummary[]>(
                `/api/admin/users${buildQuery({ search, role, includeInactive })}`
            ),
    });

    const updateUser = useMutation({
        mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
            api.patch(`/api/admin/users/${id}`, patch),
        onSuccess: () => {
            notify('Utilisateur mis à jour.', 'success');
            void queryClient.invalidateQueries({ queryKey: ['admin-users'] });
            void queryClient.invalidateQueries({ queryKey: ['admin-overview'] });
        },
        onError: (err) =>
            notify(
                err instanceof ApiError ? err.message : 'La modification a échoué.',
                'error'
            ),
    });

    const { data: etatSync } = useSyncAnnuaire();

    // Tant que l'état n'est pas chargé, la synchronisation est présumée possible :
    // désactiver le bouton par défaut ferait croire à une configuration absente.
    const configuree = etatSync?.meta?.configured ?? true;
    const derniere = etatSync?.data?.[0];

    /**
     * Le déclenchement vit ici, dans l'onglet où l'administrateur gère justement
     * les utilisateurs, et où il constate une absence dans l'annuaire. La vue
     * d'ensemble n'en porte plus qu'un renvoi : deux boutons pour une même
     * opération, dont un seul montre le résultat, seraient un piège.
     */
    const syncAnnuaire = useMutation({
        mutationFn: () => api.post<EntraSyncResult>('/api/admin/sync/entra'),
        onSuccess: (result) => {
            const { retenus, ecartes, crees, desactives } = result.users;
            notify(
                `${retenus} personne${retenus > 1 ? 's' : ''} retenue${retenus > 1 ? 's' : ''}` +
                    ` (${crees} nouvelle${crees > 1 ? 's' : ''}), ${ecartes} compte${
                        ecartes > 1 ? 's' : ''
                    } écarté${ecartes > 1 ? 's' : ''}, ${desactives} désactivé${
                        desactives > 1 ? 's' : ''
                    }.`,
                'success'
            );
            void queryClient.invalidateQueries({ queryKey: ['admin-users'] });
            void queryClient.invalidateQueries({ queryKey: ['admin-overview'] });
            void queryClient.invalidateQueries({ queryKey: ['entra-sync'] });
            // Le message disparaît, le compte rendu reste : la modale s'ouvre sur
            // le détail de ce qui vient d'être écarté, et pourquoi.
            setRapportOuvert(true);
        },
        onError: (err) => {
            if (err instanceof ApiError && err.code === 'ENTRA_SYNC_UNAVAILABLE') {
                notify(
                    'La synchronisation n’est pas configurée : la clé AZURE_CLIENT_SECRET manque côté API.',
                    'error'
                );
                return;
            }
            notify(
                err instanceof ApiError ? err.message : 'La synchronisation a échoué.',
                'error'
            );
        },
    });

    return (
        <div className="space-y-4">
            <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
                <div className="relative flex-1">
                    <Search
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-text-muted)] pointer-events-none"
                        aria-hidden
                    />
                    <input
                        type="search"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Rechercher un utilisateur…"
                        className="input pl-9"
                        aria-label="Rechercher un utilisateur"
                    />
                </div>

                <select
                    value={role}
                    onChange={(event) => setRole(event.target.value as FiltreRole)}
                    className="input sm:w-56"
                    aria-label="Filtrer par rôle dans le Hub"
                >
                    <option value="">Tous les rôles</option>
                    <option value="admin">Administrateurs</option>
                    <option value="user">Utilisateurs</option>
                </select>

                <label className="flex items-center gap-2 text-sm text-[var(--color-text)] whitespace-nowrap">
                    <input
                        type="checkbox"
                        checked={includeInactive}
                        onChange={(event) => setIncludeInactive(event.target.checked)}
                    />
                    Inclure les comptes désactivés
                </label>

                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={() => syncAnnuaire.mutate()}
                        disabled={syncAnnuaire.isPending || !configuree}
                        className="btn btn-secondary whitespace-nowrap"
                        title={
                            configuree
                                ? 'Relire l’annuaire Entra ID et mettre à jour la liste'
                                : 'Synchronisation non configurée côté API'
                        }
                    >
                        {syncAnnuaire.isPending ? (
                            <Spinner className="w-4 h-4" />
                        ) : (
                            <RefreshCw className="w-4 h-4" aria-hidden />
                        )}
                        Synchroniser l’annuaire
                    </button>

                    <button
                        type="button"
                        onClick={() => setRapportOuvert(true)}
                        className="btn btn-ghost whitespace-nowrap"
                    >
                        <ClipboardList className="w-4 h-4" aria-hidden />
                        Compte rendu
                    </button>
                </div>
            </div>

            {!configuree ? (
                /**
                 * Une absence de configuration n'est pas une panne : le dire
                 * autrement enverrait un administrateur relancer en boucle une
                 * opération qui ne peut pas aboutir.
                 */
                <p className="text-xs text-[var(--color-text-secondary)] rounded-lg border border-[var(--color-border)] px-3 py-2">
                    La synchronisation n’est pas configurée : la clé AZURE_CLIENT_SECRET manque
                    côté API, ou porte encore sa valeur de substitution. L’annuaire ne se remplit
                    alors qu’à la première connexion de chacun.
                </p>
            ) : (
                <p className="text-xs text-[var(--color-text-muted)]">
                    L’annuaire ne retient que les personnes de Clippit : adresse au motif
                    « initiale.nom », fonction renseignée dans Entra ID. Les comptes
                    d’administration, de service et les invités sont écartés.{' '}
                    {derniere ? (
                        <>
                            Dernière synchronisation {formatRelative(derniere.startedAt)} :{' '}
                            {derniere.retained} retenus, {derniere.excluded} écartés,{' '}
                            {derniere.deactivated} désactivés.
                        </>
                    ) : (
                        'Aucune synchronisation enregistrée pour l’instant.'
                    )}
                </p>
            )}

            {isLoading ? (
                <LoadingState />
            ) : isError ? (
                <ErrorState
                    message={error instanceof Error ? error.message : 'Chargement impossible.'}
                    onRetry={() => void refetch()}
                />
            ) : users.length === 0 ? (
                <EmptyState
                    title="Aucun utilisateur"
                    description="L’annuaire se remplit à la première connexion de chacun, ou par une synchronisation Entra ID."
                />
            ) : (
                <div className="card overflow-x-auto">
                    <table className="table">
                        <thead>
                            <tr>
                                <th scope="col">Utilisateur</th>
                                <th scope="col">Fonction</th>
                                <th scope="col">Rôle Hub</th>
                                <th scope="col">Accès</th>
                                <th scope="col">Dernière connexion</th>
                                <th scope="col" className="text-right">
                                    <span className="sr-only">Actions</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((user) => {
                                const isSelf = user.id === currentUser?.id;

                                return (
                                    <tr key={user.id} className={cn(!user.active && 'opacity-60')}>
                                        <td>
                                            <button
                                                type="button"
                                                onClick={() => setDetailUserId(user.id)}
                                                className="text-left"
                                            >
                                                <span className="block font-medium text-[var(--color-text)] hover:text-[var(--color-primary)]">
                                                    {user.displayName}
                                                </span>
                                                <span className="block text-xs text-[var(--color-text-muted)]">
                                                    {user.email}
                                                </span>
                                            </button>
                                        </td>
                                        <td className="text-[var(--color-text-secondary)] text-xs">
                                            {user.jobTitle ?? '-'}
                                            {user.department && (
                                                <span className="block">{user.department}</span>
                                            )}
                                        </td>
                                        <td>
                                            <span
                                                className={cn(
                                                    'badge',
                                                    user.role === 'admin'
                                                        ? 'badge-prod'
                                                        : 'badge-neutral'
                                                )}
                                            >
                                                {user.role === 'admin' ? 'Administrateur' : 'Utilisateur'}
                                            </span>
                                            {!user.active && (
                                                <span className="badge badge-missing ml-1">Désactivé</span>
                                            )}
                                            {user.entraExcludedReason && (
                                                <span
                                                    className="badge badge-pending ml-1"
                                                    title={`Hors annuaire : ${
                                                        ENTRA_REASON_LABELS[
                                                            user.entraExcludedReason
                                                        ] ?? user.entraExcludedReason
                                                    }`}
                                                >
                                                    Hors annuaire
                                                </span>
                                            )}
                                        </td>
                                        <td className="text-[var(--color-text-secondary)] tabular-nums">
                                            {user.accessCount}
                                        </td>
                                        <td className="text-[var(--color-text-secondary)] text-xs">
                                            {user.lastLogin ? formatRelative(user.lastLogin) : 'Jamais'}
                                        </td>
                                        <td className="text-right whitespace-nowrap">
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    updateUser.mutate({
                                                        id: user.id,
                                                        patch: {
                                                            role:
                                                                user.role === 'admin'
                                                                    ? 'user'
                                                                    : 'admin',
                                                        },
                                                    })
                                                }
                                                disabled={isSelf || updateUser.isPending}
                                                className="btn btn-ghost p-1.5"
                                                title={
                                                    isSelf
                                                        ? 'Vous ne pouvez pas modifier votre propre rôle'
                                                        : user.role === 'admin'
                                                          ? 'Retirer le rôle administrateur'
                                                          : 'Promouvoir administrateur'
                                                }
                                                aria-label={
                                                    user.role === 'admin'
                                                        ? `Retirer le rôle administrateur à ${user.displayName}`
                                                        : `Promouvoir ${user.displayName} administrateur`
                                                }
                                            >
                                                {user.role === 'admin' ? (
                                                    <ShieldOff className="w-4 h-4" />
                                                ) : (
                                                    <Shield className="w-4 h-4" />
                                                )}
                                            </button>

                                            <button
                                                type="button"
                                                onClick={() =>
                                                    updateUser.mutate({
                                                        id: user.id,
                                                        patch: { active: !user.active },
                                                    })
                                                }
                                                disabled={isSelf || updateUser.isPending}
                                                className="btn btn-ghost p-1.5"
                                                title={
                                                    isSelf
                                                        ? 'Vous ne pouvez pas désactiver votre propre compte'
                                                        : user.active
                                                          ? 'Désactiver ce compte'
                                                          : 'Réactiver ce compte'
                                                }
                                                aria-label={
                                                    user.active
                                                        ? `Désactiver ${user.displayName}`
                                                        : `Réactiver ${user.displayName}`
                                                }
                                            >
                                                {user.active ? (
                                                    <UserX className="w-4 h-4" />
                                                ) : (
                                                    <UserCheck className="w-4 h-4" />
                                                )}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <UserDetailModal userId={detailUserId} onClose={() => setDetailUserId(null)} />
            <SyncReportModal open={rapportOuvert} onClose={() => setRapportOuvert(false)} />
        </div>
    );
}

/**
 * Compte rendu de la synchronisation de l'annuaire.
 *
 * Répond à la question que pose une absence : ce compte a-t-il été vu, et sur
 * quel critère a-t-il été écarté ? Les critères en vigueur sont affichés à côté
 * des verdicts, parce qu'un verdict sans sa règle n'est pas vérifiable.
 */
function SyncReportModal({ open, onClose }: { open: boolean; onClose: () => void }) {
    const [runId, setRunId] = useState<string>('latest');
    const [seulementEcartes, setSeulementEcartes] = useState(true);

    const { data: runs } = useSyncAnnuaire(open);

    const {
        data: detail,
        isLoading,
        isError,
    } = useQuery({
        queryKey: ['entra-sync', 'detail', runId],
        queryFn: () => api.get<EntraSyncDetail>(`/api/admin/sync/entra/${runId}`),
        enabled: open,
        retry: false,
    });

    const criteres = detail?.criteria ?? runs?.meta?.criteria ?? null;

    const decisions = (detail?.decisions ?? []).filter(
        (decision) => !seulementEcartes || decision.decision === 'ecarte'
    );

    return (
        <Modal
            open={open}
            size="lg"
            title="Synchronisation de l’annuaire"
            description="Ce que la dernière lecture d’Entra ID a retenu, et ce qu’elle a écarté."
            onClose={onClose}
        >
            {isLoading ? (
                <LoadingState />
            ) : isError || !detail ? (
                <EmptyState
                    title="Aucune synchronisation enregistrée"
                    description="Lancez une synchronisation pour produire un compte rendu. Elle exige AZURE_CLIENT_SECRET côté API."
                />
            ) : (
                <div className="space-y-5">
                    {(runs?.data.length ?? 0) > 1 && (
                        <label className="block text-sm">
                            <span className="text-[var(--color-text-muted)] text-xs">
                                Exécution
                            </span>
                            <select
                                value={runId}
                                onChange={(event) => setRunId(event.target.value)}
                                className="input mt-1"
                            >
                                <option value="latest">La plus récente</option>
                                {runs?.data.map((run) => (
                                    <option key={run.id} value={run.id}>
                                        {formatDateTime(run.startedAt)} ·{' '}
                                        {run.trigger === 'manuel' ? 'manuelle' : 'planifiée'}
                                        {run.status === 'echec' ? ' · en échec' : ''}
                                    </option>
                                ))}
                            </select>
                        </label>
                    )}

                    <div>
                        <p className="text-xs text-[var(--color-text-muted)]">
                            {formatDateTime(detail.startedAt)}
                            {' · '}
                            {detail.trigger === 'manuel'
                                ? `déclenchée par ${detail.actorEmail ?? 'un administrateur'}`
                                : 'déclenchée par le planificateur'}
                        </p>

                        {detail.status === 'echec' && (
                            <p className="mt-2 text-sm text-[var(--color-danger)]">
                                Synchronisation en échec : {detail.errorMessage}
                            </p>
                        )}

                        <dl className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <Compteur label="Comptes examinés" value={detail.candidates} />
                            <Compteur label="Retenus" value={detail.retained} />
                            <Compteur label="Écartés" value={detail.excluded} />
                            <Compteur label="Créés" value={detail.created} />
                            <Compteur label="Mis à jour" value={detail.updated} />
                            <Compteur label="Désactivés" value={detail.deactivated} />
                            <Compteur
                                label="Signalés"
                                value={detail.flagged}
                                hint="Écartés mais conservés actifs : administrateurs du Hub ou comptes déjà connectés. À trancher à la main."
                            />
                        </dl>
                    </div>

                    {criteres && (
                        <div className="rounded-lg border border-[var(--color-border)] p-3 text-xs space-y-1">
                            <p className="font-semibold text-[var(--color-text-secondary)]">
                                Critères appliqués
                            </p>
                            <p className="text-[var(--color-text-muted)]">
                                Domaines :{' '}
                                {criteres.domaines.length > 0
                                    ? criteres.domaines.join(', ')
                                    : 'tous'}
                            </p>
                            <p className="text-[var(--color-text-muted)]">
                                Motif de l’adresse :{' '}
                                <code className="font-mono">{criteres.motifPartieLocale}</code>
                            </p>
                            <p className="text-[var(--color-text-muted)]">
                                Marqueurs écartés :{' '}
                                {criteres.marqueursTechniques.length > 0
                                    ? criteres.marqueursTechniques.join(', ')
                                    : 'aucun'}
                            </p>
                            <p className="text-[var(--color-text-muted)]">
                                Fonction renseignée exigée :{' '}
                                {criteres.fonctionObligatoire ? 'oui' : 'non'}
                            </p>
                            {criteres.exceptions.length > 0 && (
                                <p className="text-[var(--color-text-muted)]">
                                    Admis par exception : {criteres.exceptions.join(', ')}
                                </p>
                            )}
                            {criteres.interdictions.length > 0 && (
                                <p className="text-[var(--color-text-muted)]">
                                    Interdits : {criteres.interdictions.join(', ')}
                                </p>
                            )}
                            <p className="text-[var(--color-text-muted)] pt-1">
                                Ces critères se règlent par variables d’environnement
                                (ENTRA_USER_DOMAINS, ENTRA_USER_LOCAL_PATTERN, ENTRA_USER_MARKERS,
                                ENTRA_USER_REQUIRE_JOB_TITLE, ENTRA_USER_ALLOWLIST,
                                ENTRA_USER_DENYLIST).
                            </p>
                        </div>
                    )}

                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="text-sm font-semibold text-[var(--color-text)]">
                                Détail par compte
                                <span className="ml-1.5 font-normal text-[var(--color-text-muted)] tabular-nums">
                                    {decisions.length}
                                </span>
                            </h3>

                            <label className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)]">
                                <input
                                    type="checkbox"
                                    checked={seulementEcartes}
                                    onChange={(event) =>
                                        setSeulementEcartes(event.target.checked)
                                    }
                                />
                                N’afficher que les écartés
                            </label>
                        </div>

                        {decisions.length === 0 ? (
                            <p className="text-sm text-[var(--color-text-muted)]">
                                Rien à afficher pour ce filtre.
                            </p>
                        ) : (
                            <ul className="border border-[var(--color-border)] rounded-lg divide-y divide-[var(--color-border)] max-h-80 overflow-y-auto">
                                {decisions.map((decision) => (
                                    <li key={decision.azureId} className="px-3 py-2">
                                        <div className="flex items-baseline justify-between gap-3">
                                            <p className="text-sm font-medium text-[var(--color-text)] truncate">
                                                {decision.displayName ?? 'Sans nom'}
                                            </p>
                                            <span
                                                className={cn(
                                                    'badge shrink-0',
                                                    decision.decision === 'retenu'
                                                        ? 'badge-granted'
                                                        : 'badge-missing'
                                                )}
                                            >
                                                {decision.decision === 'retenu'
                                                    ? 'Retenu'
                                                    : 'Écarté'}
                                            </span>
                                        </div>
                                        <p className="text-xs text-[var(--color-text-muted)] break-all">
                                            {decision.userPrincipalName ??
                                                decision.mail ??
                                                'Aucune adresse'}
                                        </p>
                                        <p className="text-xs text-[var(--color-text-secondary)]">
                                            {ENTRA_REASON_LABELS[decision.reason] ??
                                                decision.reason}
                                            {decision.detail && (
                                                <span className="text-[var(--color-text-muted)]">
                                                    {' · '}
                                                    {decision.detail}
                                                </span>
                                            )}
                                        </p>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            )}
        </Modal>
    );
}

function Compteur({ label, value, hint }: { label: string; value: number; hint?: string }) {
    return (
        <div title={hint}>
            <dt className="text-[var(--color-text-muted)] text-xs">{label}</dt>
            <dd className="text-lg font-semibold text-[var(--color-text)] tabular-nums">
                {value}
            </dd>
        </div>
    );
}

function UserDetailModal({ userId, onClose }: { userId: string | null; onClose: () => void }) {
    const { data, isLoading } = useQuery({
        queryKey: ['admin-user', userId],
        queryFn: () => api.get<HubUserDetail>(`/api/admin/users/${userId}`),
        enabled: Boolean(userId),
    });

    if (!userId) return null;

    return (
        <Modal
            open
            size="lg"
            title={data?.displayName ?? 'Utilisateur'}
            description={data?.email}
            onClose={onClose}
        >
            {isLoading || !data ? (
                <LoadingState />
            ) : (
                <div className="space-y-4">
                    <dl className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                            <dt className="text-[var(--color-text-muted)] text-xs">Fonction</dt>
                            <dd className="text-[var(--color-text)]">{data.jobTitle ?? '-'}</dd>
                        </div>
                        <div>
                            <dt className="text-[var(--color-text-muted)] text-xs">Service</dt>
                            <dd className="text-[var(--color-text)]">{data.department ?? '-'}</dd>
                        </div>
                        <div>
                            <dt className="text-[var(--color-text-muted)] text-xs">Rôle Hub</dt>
                            <dd className="text-[var(--color-text)]">
                                {data.role === 'admin' ? 'Administrateur' : 'Utilisateur'}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-[var(--color-text-muted)] text-xs">
                                Dernière connexion
                            </dt>
                            <dd className="text-[var(--color-text)]">
                                {data.lastLogin ? formatRelative(data.lastLogin) : 'Jamais'}
                            </dd>
                        </div>
                    </dl>

                    {data.entraExcludedReason && (
                        <p className="text-sm text-[var(--color-text-secondary)] rounded-lg border border-[var(--color-border)] p-3">
                            <span className="font-semibold">Hors annuaire.</span>{' '}
                            {ENTRA_REASON_LABELS[data.entraExcludedReason] ??
                                data.entraExcludedReason}
                            {data.entraSyncedAt && (
                                <span className="text-[var(--color-text-muted)]">
                                    {' '}
                                    (dernière synchronisation de ce compte le{' '}
                                    {formatDate(data.entraSyncedAt)})
                                </span>
                            )}
                        </p>
                    )}

                    <div>
                        <h3 className="text-sm font-semibold text-[var(--color-text)] mb-2">
                            Applications accessibles
                            <span className="ml-1.5 font-normal text-[var(--color-text-muted)] tabular-nums">
                                {data.access.length}
                            </span>
                        </h3>

                        {data.access.length === 0 ? (
                            <p className="text-sm text-[var(--color-text-muted)]">
                                Aucun accès attribué.
                            </p>
                        ) : (
                            <ul className="border border-[var(--color-border)] rounded-lg divide-y divide-[var(--color-border)]">
                                {data.access.map((access) => (
                                    <li
                                        key={access.applicationId}
                                        className="flex items-center gap-3 px-3 py-2"
                                    >
                                        <span
                                            className="app-swatch flex items-center justify-center w-7 h-7 rounded-lg shrink-0"
                                            style={appColor(access.color)}
                                            aria-hidden
                                        >
                                            <AppIcon name={access.icon} className="w-3.5 h-3.5" />
                                        </span>

                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-[var(--color-text)] truncate">
                                                {access.applicationName}
                                            </p>
                                            <p className="text-xs text-[var(--color-text-muted)]">
                                                {ACCESS_SOURCE_LABELS[access.source]}
                                                {access.expiresAt &&
                                                    ` · jusqu’au ${formatDate(access.expiresAt)}`}
                                            </p>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            )}
        </Modal>
    );
}
